import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Bookmark, Loader2, MapPin, Navigation, X } from "lucide-react";

import { cn } from "@/modern/utils";
import { Popover, PopoverAnchor, PopoverContent } from "@/modern/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/modern/components/ui/command";
import { useSwivuLocation, type GooglePlaceDetails } from "@/react/hooks/entityHooks/useSwivuLocation";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { Location } from "@/bryntum-lib/models/Location";
import { LocationReadinessBadge } from "./LocationReadinessBadge";

// Re-export for consumers that previously imported from here
export type { GooglePlaceDetails };

// ─── Props ────────────────────────────────────────────────────────────────────

interface AddressAutoCompleteProps {
  /** Controlled value — the committed street address string. */
  value: string;
  /** Called on every raw keystroke so the parent can track the uncommitted input. */
  onChange: (value: string) => void;
  /** Called when the user picks a saved Location from the store results. */
  onSelectSavedLocation: (loc: Location) => void;
  /** Called with resolved place details when the user picks a Google Places result. */
  onSelectGooglePlace: (details: GooglePlaceDetails) => void;
  /**
   * Called when the user picks the "Set <query>" fallback option.
   * The confirm dialog is the parent's responsibility.
   */
  onSetManual: (address: string) => void;
  /** Whether to show the clear (X) button when the field has a value. Defaults to true. */
  showClear?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// ─── Shared CommandItem className ─────────────────────────────────────────────

const itemCls =
  "flex w-full cursor-pointer items-center gap-2.5 rounded-none px-3 py-1.5 text-left aria-selected:bg-accent data-[selected=true]:bg-accent";

// ─── Component ────────────────────────────────────────────────────────────────

export const AddressAutoComplete = ({
  value,
  onChange,
  onSelectSavedLocation,
  onSelectGooglePlace,
  onSetManual,
  showClear = true,
  placeholder = "Search for a location…",
  disabled = false,
  className,
}: AddressAutoCompleteProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = React.useState(value);
  const [isFocused, setIsFocused] = React.useState(false);
  const justSelectedRef = React.useRef(false);
  /** Snapshot of the committed value at the moment the input was focused. Used to revert on blur without selection. */
  const valueAtFocusRef = React.useRef(value);

  const { project } = useSwivuBryntumContext();

  const {
    searchResults,
    onSearchAddress,
    fetchGooglePlaceDetails,
    isLoading,
    clearSearchResults,
  } = useSwivuLocation();

  // Keep local input in sync when parent commits a new value
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  // ─── Saved location matches ─────────────────────────────────────────────────

  const savedMatches = React.useMemo<Location[]>(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q || !project?.locationStore) return [];
    return project.locationStore.query((loc: Location) =>
      loc.streetAddress?.toLowerCase().includes(q) ||
      loc.propertyName?.toLowerCase().includes(q) ||
      loc.city?.toLowerCase().includes(q)
    );
  }, [inputValue, project]);

  const hasResults = savedMatches.length > 0 || searchResults.length > 0;
  const showSetFallback = !isLoading && !hasResults && inputValue.trim().length > 0;
  const showDropdown = isFocused && !disabled && inputValue.trim().length > 0;

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleSelectSaved = React.useCallback(
    (loc: Location) => {
      justSelectedRef.current = true;
      setInputValue(loc.streetAddress ?? "");
      clearSearchResults();
      setIsFocused(false);
      onSelectSavedLocation(loc);
    },
    [clearSearchResults, onSelectSavedLocation]
  );

  const handleSelectGoogle = React.useCallback(
    async (placeId: string) => {
      justSelectedRef.current = true;
      const label = searchResults.find((r) => r.value === placeId)?.label ?? "";
      setInputValue(label);
      setIsFocused(false);

      try {
        // Fetch before clearing — the place instance lives in googlePlaceSuggestions.current
        const details = await fetchGooglePlaceDetails(placeId);
        clearSearchResults();
        onSelectGooglePlace(details);
        setInputValue(details.formattedAddress);
      } catch (err) {
        console.error("Failed to fetch Google place details:", err);
        clearSearchResults();
      }
    },
    [searchResults, fetchGooglePlaceDetails, clearSearchResults, onSelectGooglePlace]
  );

  const handleSetManual = React.useCallback(() => {
    justSelectedRef.current = true;
    setIsFocused(false);
    clearSearchResults();
    onSetManual(inputValue.trim());
  }, [inputValue, clearSearchResults, onSetManual]);

  const handleClear = React.useCallback(() => {
    justSelectedRef.current = true; // prevents handleBlur from reverting to the old value
    setInputValue("");
    onChange("");
    clearSearchResults();
    setIsFocused(false);
  }, [onChange, clearSearchResults]);

  const handleBlur = React.useCallback(() => {
    setIsFocused(false);
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    // Revert to the value that was committed when this focus session began
    const committed = valueAtFocusRef.current;
    setInputValue(committed);
    onChange(committed);
  }, [onChange]);

  const handleEscape = React.useCallback(() => {
    setIsFocused(false);
    const committed = valueAtFocusRef.current;
    setInputValue(committed);
    onChange(committed);
  }, [onChange]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={cn("relative w-full", className)}>
      {/*
       * Command wraps BOTH the input (PopoverAnchor) and the list (PopoverContent).
       * React context flows through Portals, so cmdk's keyboard navigation works
       * across the Popover portal boundary.
       */}
      <Command shouldFilter={false} className="overflow-visible bg-transparent">
        <Popover open={showDropdown}>
          <PopoverAnchor asChild>
            <div className="relative w-full">
              <CommandPrimitive.Input
                ref={inputRef}
                value={inputValue}
                onValueChange={(next) => {
                  setInputValue(next);
                  onChange(next);
                  onSearchAddress(next);
                  setIsFocused(true);
                }}
                onFocus={() => {
                  valueAtFocusRef.current = value;
                  setIsFocused(true);
                  if (inputValue.trim()) onSearchAddress(inputValue);
                }}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    handleEscape();
                  }
                  // Tab while dropdown is open: keep focus so arrow keys can navigate
                  if (e.key === "Tab" && showDropdown) {
                    e.preventDefault();
                  }
                }}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete="off"
                className={cn(
                  "rounded-md text-base placeholder:text-muted-foreground outline-none focus:outline-none focus:ring-0 focus:border-input disabled:cursor-not-allowed disabled:opacity-50 border border-input bg-transparent h-swivu-input px-3 py-2 w-full",
                  showClear && inputValue.trim() && !disabled && "pr-9"
                )}
              />

              {showClear && inputValue.trim() && !disabled && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center opacity-50 transition-opacity hover:opacity-100"
                  title="Clear location"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </PopoverAnchor>

          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] max-w-none p-0 z-[--swivu-bryntum-z-index]"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onMouseDown={(e) => e.preventDefault()}
            align="start"
            sideOffset={4}
          >
            <CommandList className="max-h-80 py-1.5">

              {/* ── Saved locations section ────────────────────────────── */}
              {savedMatches.length > 0 && (
                <CommandGroup className="p-0">
                  <p className="px-3 pt-1.5 pb-1 text-xs text-muted-foreground/50">Saved locations</p>
                  {savedMatches.map((loc) => (
                    <CommandItem
                      key={String(loc.id)}
                      value={`saved-${loc.id}`}
                      onSelect={() => handleSelectSaved(loc)}
                      className={itemCls}
                    >
                      <Bookmark className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-foreground">
                          {loc.propertyName || loc.streetAddress || "Unnamed location"}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {[loc.streetAddress, loc.city, loc.stateOrRegion].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <LocationReadinessBadge locationRecord={loc} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Loading skeleton — while Google is fetching with no results yet */}
              {isLoading && searchResults.length === 0 && (
                <CommandGroup className={cn("p-0", savedMatches.length > 0 && "mt-1 border-t border-border pt-1")}>
                  <p className="px-3 pt-1.5 pb-1 text-xs text-muted-foreground/50">Address suggestions</p>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2.5 px-3 py-1.5">
                      <div className="h-3.5 w-3.5 shrink-0 rounded-full bg-muted-foreground/15 animate-pulse" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 rounded bg-muted-foreground/15 animate-pulse" style={{ width: `${55 + (i % 3) * 15}%` }} />
                        <div className="h-2.5 rounded bg-muted-foreground/10 animate-pulse" style={{ width: `${35 + (i % 4) * 10}%` }} />
                      </div>
                    </div>
                  ))}
                </CommandGroup>
              )}

              {/* ── Google Places section ──────────────────────────────── */}
              {searchResults.length > 0 && (
                <>
                  {savedMatches.length > 0 && <CommandSeparator className="my-1" />}
                  <CommandGroup className="p-0">
                    <div className="flex items-center justify-between px-3 pt-1.5 pb-1">
                      <p className="text-xs text-muted-foreground/50">Address suggestions</p>
                      {isLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground/50" />}
                    </div>
                    {searchResults.map((result) => (
                      <CommandItem
                        key={result.value}
                        value={`google-${result.value}`}
                        onSelect={() => handleSelectGoogle(result.value)}
                        className={itemCls}
                      >
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-foreground">
                            {result.label.split(",")[0]}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {result.label.split(",").slice(1).join(",").trim()}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {/* ── "Set <query>" fallback ─────────────────────────────── */}
              {showSetFallback && (
                <CommandGroup className="p-0">
                  <CommandItem
                    value={`set-${inputValue.trim()}`}
                    onSelect={handleSetManual}
                    className={itemCls}
                  >
                    <Navigation className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-foreground">
                        Set &ldquo;{inputValue.trim()}&rdquo;
                      </div>
                    </div>
                  </CommandItem>
                </CommandGroup>
              )}

            </CommandList>
          </PopoverContent>
        </Popover>
      </Command>
    </div>
  );
};

export default AddressAutoComplete;
